import React from "react";
import { Clock } from "lucide-react";
import { formatDuration } from "../../../utils/time";
import { formatLabel } from "../../../utils/formatLabel";
import { getActiveTokens } from "../../../utils/planVisuals";
import { cn } from "../../../lib/utils";

/**
 * Compact timing strip for the equipment (ERG) dashboards. Mirrors the Coach
 * footer's vocabulary — phase time remaining as the headline, phase label
 * above, total-remaining + "Next: …" below — so swiping between the Coach and
 * ERG panes reads consistently.
 *
 * Purely presentational; re-renders at 1 Hz off the coach tick while the
 * dashboard's metric tiles stay ref-painted and untouched. Render nothing when
 * no guided session is driving the clock (standalone equipment use).
 *
 * `action` (the pause button, #197 follow-up) sits at the right of the strip,
 * clear of every figure. A reps step has no clock, so the strip shrinks to
 * its name and the button.
 */
const DashboardTimingHeader = ({
  phaseType,
  phaseLabel,
  timeLeftInPhase,
  totalRemaining,
  nextUpLabel,
  action = null,
}) => {
  if (timeLeftInPhase == null && !action) return null;

  const tokens = getActiveTokens(phaseType, phaseLabel);

  if (timeLeftInPhase == null) {
    return (
      <div className="flex min-h-[44px] items-center justify-between gap-3">
        <span className={cn("min-w-0 truncate text-xs font-bold uppercase tracking-widest", tokens.text)}>
          {phaseLabel ? formatLabel(phaseLabel) : ""}
        </span>
        {action}
      </div>
    );
  }

  return (
    <div className="relative flex flex-col items-center gap-0.5 px-12 text-center">
      {action && <div className="absolute right-0 top-1/2 -translate-y-1/2">{action}</div>}
      {phaseLabel && (
        <span
          className={cn(
            "text-xs font-bold uppercase tracking-widest",
            tokens.text,
          )}
        >
          {formatLabel(phaseLabel)}
        </span>
      )}
      <div
        className={cn(
          "flex items-center gap-1.5 text-5xl font-timer font-bold tabular-nums leading-none",
          tokens.text,
        )}
      >
        <Clock className="w-6 h-6" />
        <span>{formatDuration(timeLeftInPhase)}</span>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-x-2 text-xs text-white/45">
        {totalRemaining > 0 && (
          <span className="tabular-nums">
            {formatDuration(totalRemaining)} left
          </span>
        )}
        {totalRemaining > 0 && nextUpLabel && (
          <span className="text-white/25">·</span>
        )}
        {nextUpLabel && <span>Next: {formatLabel(nextUpLabel)}</span>}
      </div>
    </div>
  );
};

export default DashboardTimingHeader;
